import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Zap, ShieldCheck } from 'lucide-react';
import { NovaCoin3D } from './NovaCoin3D.js';

interface MinerActivationEffectProps {
  triggerKey: number; // Incrementing counter to trigger activation effect
  minerName?: string;
}

export const MinerActivationEffect: React.FC<MinerActivationEffectProps> = ({
  triggerKey,
  minerName = 'Stars Miner (100k/24h)',
}) => {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (triggerKey <= 0) return;

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      return;
    }

    setActive(true);
    const timer = setTimeout(() => {
      setActive(false);
    }, 2400);

    return () => clearTimeout(timer);
  }, [triggerKey]);

  if (!active) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center overflow-hidden select-none"
      aria-hidden="true"
    >
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.15 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            className="flex flex-col items-center justify-center relative"
          >
            {/* Primary Golden Shockwave Ring */}
            <motion.div
              initial={{ scale: 0.2, opacity: 0.95 }}
              animate={{ scale: 3.8, opacity: 0 }}
              transition={{ duration: 1.5, ease: [0.1, 0.9, 0.2, 1] }}
              className="absolute w-44 h-44 rounded-full border-2 border-amber-300 shadow-[0_0_35px_rgba(245,158,11,0.85)]"
            />

            {/* Secondary Violet & Cyan Orbital Halo Shockwave */}
            <motion.div
              initial={{ scale: 0.1, opacity: 0.85 }}
              animate={{ scale: 2.8, opacity: 0 }}
              transition={{ duration: 1.3, delay: 0.12, ease: 'easeOut' }}
              className="absolute w-44 h-44 rounded-full border border-cyan-400 shadow-[0_0_25px_rgba(6,182,212,0.65)]"
            />

            {/* Tertiary Micro Shockwave */}
            <motion.div
              initial={{ scale: 0.05, opacity: 1 }}
              animate={{ scale: 2.0, opacity: 0 }}
              transition={{ duration: 1.0, delay: 0.22, ease: 'easeOut' }}
              className="absolute w-44 h-44 rounded-full border border-yellow-200"
            />

            {/* Central Suspended 3D Gold Coin with Specular Illumination */}
            <motion.div
              initial={{ scale: 0, rotateY: -180 }}
              animate={{ scale: [0, 1.25, 1], rotateY: 0 }}
              transition={{ duration: 0.7, ease: [0.175, 0.885, 0.32, 1.275] }}
              className="relative z-10 flex items-center justify-center p-3 rounded-full bg-midnight-950/80 border-2 border-amber-400/80 shadow-[0_0_45px_rgba(245,158,11,0.9)] backdrop-blur-md"
            >
              <NovaCoin3D size="xl" animate={true} speed="fast" />
            </motion.div>

            {/* VIP Status Pill Banner */}
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.4 }}
              className="mt-5 px-6 py-2.5 rounded-full bg-space-950/95 border border-amber-400/70 shadow-2xl backdrop-blur-xl flex items-center space-x-2.5"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <Sparkles className="w-4 h-4 text-nova-gold animate-spin" />
              <span className="font-black text-white text-xs tracking-wider uppercase">
                {minerName} Verified & Online!
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
